import { asAreaUnit, type AreaUnit } from '../../utils/units';
import { areaWords, SERVICE_AREA_LIMITS } from '../../utils/serviceUnits';
import { ServiceSiteKind, type IServiceSiteInput } from '../../../__generated__/graphql';
import type { IServiceSiteFieldsFragment } from '../../services/serviceService/__generated__/queries.generated';
import { EMPTY_ADDRESS, type StayAddress } from '../StayHostPage/stayAddress';

/**
 * A customer's home or vehicle (spec 019 W14, data-model §2.5). Pure; limits
 * match the backend `service-site-rules.ts`. The access instructions are only
 * sent when the customer changed them, since leaving them out keeps them.
 */

export type ServiceSite = IServiceSiteFieldsFragment;

export const VEHICLE_CLASS_OPTIONS = [
  { value: '', label: 'Pick from the make and model' },
  { value: 'car', label: 'Car' },
  { value: 'suv_truck', label: 'SUV or truck' },
  { value: 'large', label: '3-row SUV or van' },
  { value: 'luxury', label: 'Luxury' },
] as const;

export interface SiteForm {
  kind: ServiceSiteKind;
  nickname: string;
  address: StayAddress;
  // home
  bedrooms: string;
  bathrooms: string;
  /** In `areaUnit` (spec 028). */
  sqft: string;
  /** Spec 028: the home's own unit, else the brand's; always sent with the area. */
  areaUnit: AreaUnit;
  floors: string;
  pets: boolean;
  vacuum: boolean;
  washer: boolean;
  dryer: boolean;
  linenSets: string;
  linensLocation: string;
  consumablesLocation: string;
  // vehicle
  make: string;
  model: string;
  year: string;
  color: string;
  plate: string;
  /** Empty: the server guesses it from the make and model. */
  vehicleClass: string;
  linkedCarId: string | null;
  accessInstructions: string;
  accessChanged: boolean;
  // home: Airbnb or Vrbo calendar (D19, W28)
  calendarUrl: string;
  calendarWorkerCarId: string;
  calendarOfferingId: string;
  calendarWindowStart: string;
  calendarWindowEnd: string;
  /** Add-on code to quantity. */
  calendarAddOns: Record<string, number>;
  /** Only sent when changed, so a save elsewhere keeps the calendar. */
  calendarChanged: boolean;
}

export type SiteFormErrors = Partial<Record<keyof SiteForm | 'form', string>>;

export function emptySiteForm(
  kind: ServiceSiteKind = ServiceSiteKind.Home,
  areaUnit: AreaUnit = 'sqft',
): SiteForm {
  return {
    kind,
    nickname: '',
    address: { ...EMPTY_ADDRESS },
    bedrooms: '1',
    bathrooms: '1',
    sqft: '',
    areaUnit,
    floors: '',
    pets: false,
    vacuum: false,
    washer: false,
    dryer: false,
    linenSets: '',
    linensLocation: '',
    consumablesLocation: '',
    make: '',
    model: '',
    year: '',
    color: '',
    plate: '',
    vehicleClass: '',
    linkedCarId: null,
    accessInstructions: '',
    accessChanged: false,
    calendarUrl: '',
    calendarWorkerCarId: '',
    calendarOfferingId: '',
    calendarWindowStart: '11:00',
    calendarWindowEnd: '15:00',
    calendarAddOns: {},
    calendarChanged: false,
  };
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export function siteToForm(
  site: ServiceSite,
  accessInstructions?: string | null,
  brandAreaUnit: AreaUnit = 'sqft',
): SiteForm {
  const home = (site.homeDetails ?? {}) as Record<string, any>;
  const supplies = (site.homeSupplies ?? {}) as Record<string, any>;
  const vehicle = (site.vehicleDetails ?? {}) as Record<string, any>;
  return {
    ...emptySiteForm(site.kind),
    nickname: site.nickname,
    address: {
      street: site.addressLine1,
      unit: site.addressLine2 ?? '',
      city: site.city,
      state: site.state,
      zipCode: site.zip,
      latitude: site.latitude,
      longitude: site.longitude,
    },
    bedrooms: str(home.bedrooms ?? 1),
    bathrooms: str(home.bathrooms ?? 1),
    sqft: str(home.sqft),
    // A saved area without a unit is in square feet; a home with no area takes the brand's.
    areaUnit: home.areaUnit
      ? asAreaUnit(home.areaUnit)
      : home.sqft != null
        ? 'sqft'
        : brandAreaUnit,
    floors: str(home.floors),
    pets: Boolean(home.pets),
    vacuum: Boolean(supplies.vacuum),
    washer: Boolean(supplies.washer),
    dryer: Boolean(supplies.dryer),
    linenSets: str(supplies.linenSets),
    linensLocation: str(supplies.linensLocation),
    consumablesLocation: str(supplies.consumablesLocation),
    make: str(vehicle.make),
    model: str(vehicle.model),
    year: str(vehicle.year),
    color: str(vehicle.color),
    plate: str(vehicle.plate),
    vehicleClass: str(vehicle.vehicleClass),
    linkedCarId: vehicle.linkedCarId ?? null,
    accessInstructions: accessInstructions ?? '',
    ...calendarToForm(site),
  };
}

/** The saved calendar link and defaults as form fields. */
export function calendarToForm(
  site: Pick<ServiceSite, 'externalCalendarUrl' | 'calendarDefaults'>,
): Pick<
  SiteForm,
  | 'calendarUrl'
  | 'calendarWorkerCarId'
  | 'calendarOfferingId'
  | 'calendarWindowStart'
  | 'calendarWindowEnd'
  | 'calendarAddOns'
> {
  const d = (site.calendarDefaults ?? {}) as Record<string, any>;
  const addOns: Record<string, number> = {};
  for (const a of Array.isArray(d.addOns) ? d.addOns : []) {
    if (a && typeof a.code === 'string') addOns[a.code] = Number(a.quantity ?? 1) || 1;
  }
  return {
    calendarUrl: site.externalCalendarUrl ?? '',
    calendarWorkerCarId: str(d.workerCarId),
    calendarOfferingId: str(d.offeringId),
    calendarWindowStart: str(d.windowStart) || '11:00',
    calendarWindowEnd: str(d.windowEnd) || '15:00',
    calendarAddOns: addOns,
  };
}

const HALF_HOUR = /^([01]\d|2[0-3]):(00|30)$/;

/** D19: the calendar link and its defaults, or the problems by form field. */
export function calendarInput(form: SiteForm): {
  externalCalendarUrl: string | null;
  calendarDefaults: Record<string, unknown> | null;
  errors: SiteFormErrors;
} {
  const errors: SiteFormErrors = {};
  const url = form.calendarUrl.trim();
  if (!url) return { externalCalendarUrl: null, calendarDefaults: null, errors };
  if (!/^https:\/\/\S+$/i.test(url))
    errors.calendarUrl = 'The calendar link must start with https://';
  if (!form.calendarWorkerCarId) errors.calendarWorkerCarId = 'Choose a worker';
  if (!form.calendarOfferingId) errors.calendarOfferingId = 'Choose the service';
  if (!HALF_HOUR.test(form.calendarWindowStart)) {
    errors.calendarWindowStart = 'A time on the hour or half hour';
  }
  if (!HALF_HOUR.test(form.calendarWindowEnd)) {
    errors.calendarWindowEnd = 'A time on the hour or half hour';
  } else if (form.calendarWindowEnd <= form.calendarWindowStart) {
    errors.calendarWindowEnd = 'Ends after it starts, the same day';
  }
  const addOns = Object.entries(form.calendarAddOns)
    .filter(([, quantity]) => quantity > 0)
    .map(([code, quantity]) => ({ code, quantity: Math.min(50, Math.round(quantity)) }));
  return {
    externalCalendarUrl: url,
    calendarDefaults: {
      workerCarId: form.calendarWorkerCarId,
      offeringId: form.calendarOfferingId,
      windowStart: form.calendarWindowStart,
      windowEnd: form.calendarWindowEnd,
      addOns,
    },
    errors,
  };
}

/** Server field paths (`details.errors[].field`) to form fields. */
export const CALENDAR_FIELD_OF: Record<string, keyof SiteForm> = {
  externalCalendarUrl: 'calendarUrl',
  calendarDefaults: 'calendarUrl',
  'calendarDefaults.workerCarId': 'calendarWorkerCarId',
  'calendarDefaults.offeringId': 'calendarOfferingId',
  'calendarDefaults.windowStart': 'calendarWindowStart',
  'calendarDefaults.windowEnd': 'calendarWindowEnd',
  'calendarDefaults.addOns': 'calendarOfferingId',
};

function intIn(text: string, min: number, max: number): number | null | undefined {
  if (!text.trim()) return undefined;
  const n = Number(text);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

/** The `ServiceSiteInput`, or the problems to show next to the boxes. */
export function toSiteInput(form: SiteForm): { input: IServiceSiteInput; errors: SiteFormErrors } {
  const errors: SiteFormErrors = {};
  const a = form.address;
  if (!form.nickname.trim() || form.nickname.trim().length > 60) {
    errors.nickname = 'Give it a name of 1 to 60 characters, like "Beach house"';
  }
  if (a.street.trim().length < 3) errors.address = 'Enter the street address';
  else if (!a.city.trim()) errors.address = 'Enter the city';
  else if (!/^[A-Za-z]{2}$/.test(a.state.trim())) errors.address = 'Use the two-letter state code';
  else if (!/^\d{5}(-\d{4})?$/.test(a.zipCode.trim())) errors.address = 'Enter a US ZIP code';

  const input: IServiceSiteInput = {
    kind: form.kind,
    nickname: form.nickname.trim(),
    addressLine1: a.street.trim(),
    addressLine2: a.unit.trim() || null,
    city: a.city.trim(),
    state: a.state.trim().toUpperCase(),
    zip: a.zipCode.trim(),
  };
  if (a.latitude !== null && a.longitude !== null) {
    input.latitude = a.latitude;
    input.longitude = a.longitude;
  }

  if (form.kind === ServiceSiteKind.Home) {
    const bedrooms = intIn(form.bedrooms, 0, 10);
    const bathrooms = Number(form.bathrooms);
    const areaUnit = form.areaUnit ?? 'sqft';
    const area = SERVICE_AREA_LIMITS[areaUnit];
    const sqft = intIn(form.sqft, area.minArea, area.maxArea);
    const floors = intIn(form.floors, 1, 5);
    const linenSets = intIn(form.linenSets, 0, 50);
    if (bedrooms === null || bedrooms === undefined) errors.bedrooms = '0 (studio) to 10';
    if (!(bathrooms >= 0.5 && bathrooms <= 8 && Number.isInteger(bathrooms * 2))) {
      errors.bathrooms = '0.5 to 8, in halves';
    }
    if (sqft === null) {
      errors.sqft = `${area.minArea.toLocaleString('en-US')} to ${area.maxArea.toLocaleString('en-US')} ${areaWords(areaUnit)}`;
    }
    if (floors === null) errors.floors = '1 to 5';
    if (linenSets === null) errors.linenSets = '0 to 50';
    for (const key of ['linensLocation', 'consumablesLocation'] as const) {
      if (form[key].length > 200) errors[key] = 'Up to 200 characters';
    }
    input.homeDetails = {
      bedrooms,
      bathrooms,
      sqft: sqft ?? null,
      areaUnit,
      floors: floors ?? null,
      pets: form.pets,
    };
    input.homeSupplies = {
      vacuum: form.vacuum,
      washer: form.washer,
      dryer: form.dryer,
      linenSets: linenSets ?? null,
      linensLocation: form.linensLocation.trim() || null,
      consumablesLocation: form.consumablesLocation.trim() || null,
    };
  } else {
    const year = intIn(form.year, 1950, new Date().getFullYear() + 2);
    if (year === null) errors.year = 'Enter a model year';
    if (!form.make.trim() && !form.vehicleClass) errors.make = 'Enter the make, or pick the size';
    if (form.plate.length > 12) errors.plate = 'Up to 12 characters';
    input.vehicleDetails = {
      make: form.make.trim() || null,
      model: form.model.trim() || null,
      year: year ?? null,
      color: form.color.trim() || null,
      plate: form.plate.trim() || null,
      ...(form.vehicleClass ? { vehicleClass: form.vehicleClass } : {}),
      ...(form.linkedCarId ? { linkedCarId: form.linkedCarId } : {}),
    };
  }

  if (form.kind === ServiceSiteKind.Home && form.calendarChanged) {
    const calendar = calendarInput(form);
    Object.assign(errors, calendar.errors);
    input.externalCalendarUrl = calendar.externalCalendarUrl;
    input.calendarDefaults = calendar.calendarDefaults;
  }

  if (form.accessChanged) {
    if (form.accessInstructions.length > 2000) errors.accessInstructions = 'Up to 2,000 characters';
    input.accessInstructions = form.accessInstructions.trim() || null;
  }
  return { input, errors };
}

/** "3 bd · 2 ba" or "2021 Toyota RAV4". */
export function siteSummary(site: Pick<ServiceSite, 'kind' | 'homeDetails' | 'vehicleDetails'>) {
  if (site.kind === ServiceSiteKind.Home) {
    const home = (site.homeDetails ?? {}) as Record<string, any>;
    return `${home.bedrooms === 0 ? 'Studio' : `${home.bedrooms ?? '?'} bd`} · ${home.bathrooms ?? '?'} ba`;
  }
  const v = (site.vehicleDetails ?? {}) as Record<string, any>;
  return [v.year, v.make, v.model].filter(Boolean).join(' ') || 'Vehicle';
}

/** The vehicle saved for this car, if any (`vehicleDetails.linkedCarId`). */
export function linkedVehicleSite(
  sites: readonly ServiceSite[],
  linkedCarId: string,
): ServiceSite | null {
  return (
    sites.find(
      s =>
        s.isActive &&
        s.kind === ServiceSiteKind.Vehicle &&
        (s.vehicleDetails as any)?.linkedCarId === linkedCarId,
    ) ?? null
  );
}
